import React, { useEffect, useState } from 'react';
import { CustomerService } from '@/account/CustomerService/CustomerService';
import { useAccount } from '@/account/useAccount';
import { CancelFinalStepModal } from '@/components/CancelFinalStepModal/CancelFinalStepModal';
import { ConfirmCancelModal } from '@/components/ConfirmCancelModal/ConfirmCancelModal';
import { MobileAppsCtaButtons } from '@/components/MobileAppsCtaCard/MobileAppsCtaButtons';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  Text,
  Stack,
  Link,
  Divider,
  CustomIcons,
  Avatar,
  Box,
  Grid,
} from '@/theme/index';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { StyledLink, StyledBox, NewTag } from './components';
import { AccessibilityWidget } from '../accessibility/AccessibilityWidget';
import { useScreenSize } from '../utils';

interface FooterLink {
  href: string | undefined;
  textContent: string;
  clickHandler?: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => void;
}

interface FooterProps {
  /**
   * Event handler for the "Contact Us" link.
   * @param {React.MouseEvent<HTMLAnchorElement>} event - The mouse event when the link is clicked.
   */
  contactUsHandler: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Optional custom disclaimer JSX element to be displayed in the footer.
   */
  customDisclaimer?: JSX.Element;
  /**
   * Component to render the About Us links.
   */
  AboutUsLinks: React.ElementType;
  /**
   * Component to render the Tools links.
   */
  ToolsLinks?: React.ElementType;
  /**
   * Component to render the Search links.
   */
  SearchLinks?: React.ElementType;
  /**
   * Optional boolean to determine whether or not to display the mobile apps CTA.
   */
  hasMobileApps?: boolean;
  /**
   * Optional boolean to determine whether or not to display the privacy asset and norton icon.
   */
  showSecurityInfo?: boolean;
  /**
   * Component to render the dynamic help links.
   */
  DynamicHelpLinks?: React.ElementType;
  /** Optional string to determine where should we do the legal name redirection.
   */
  legalNamePathToRedirect?: string;
  /** Optional boolean to determine whether or not to display the customer service information.
   */
  showCustomerServiceInfo?: boolean;
}

/**
 * Renders the footer component with various links and information.
 * @param {FooterProps} props - The props for the Footer component.
 */
export const Footer: React.FC<FooterProps> = ({
  contactUsHandler,
  customDisclaimer,
  AboutUsLinks,
  ToolsLinks,
  SearchLinks,
  hasMobileApps,
  DynamicHelpLinks,
  showSecurityInfo = true,
  legalNamePathToRedirect,
  showCustomerServiceInfo = true,
}) => {
  const currentYear = new Date().getFullYear();
  const {
    data: { legalName, brandName },
    links: {
      fcraLink,
      doDontsLink,
      tosLink,
      privacyLink,
      supportLink,
      FaqLink,
      dontSellInfoLink,
      affiliateLink,
      partnerProgramLink,
      propertyAdvertiseLink,
    },
    config: { socialMedia },
    variationPlanNames,
    internationalPlanNames,
  } = useAppConstantsContext();
  const { isMobile } = useScreenSize();
  const { redirect } = useRedirect();
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);
  const [isCancelFinalStepModalOpen, setIsCancelFinalStepModalOpen] =
    useState(false);

  const helpLinks: FooterLink[] = [
    {
      href: supportLink,
      textContent: 'Customer Care',
    },
    {
      href: '#',
      textContent: 'Contact Us',
      clickHandler: contactUsHandler,
    },
    {
      href: doDontsLink,
      textContent: "Do's & Don'ts",
    },
    {
      href: FaqLink,
      textContent: 'FAQ',
    },
    {
      href: dontSellInfoLink,
      textContent: 'Do Not Sell or Share My Personal Information',
    },
    {
      href: affiliateLink,
      textContent: 'Affiliates',
    },
    {
      href: partnerProgramLink,
      textContent: 'Partner Program',
    },
  ];

  const { data: account } = useAccount(true);

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames
        )
      : null;

  const isInInternationalLanguageTest =
    reportVariations?.isInInternationalLanguageTest ?? false;
  const isInternationalVinTest =
    reportVariations?.isInternationalVinTest ?? false;

  if (propertyAdvertiseLink) {
    helpLinks.push({
      href: propertyAdvertiseLink,
      textContent: 'Advertise',
    });
  }

  useEffect(() => {
    const cancelAccount = new URLSearchParams(window.location.search);
    const isContactUs = cancelAccount.get('cancelAccount');

    if (isContactUs === 'true') {
      setIsConfirmCancelModalOpen(true);
    }
  }, []);

  const handleOnClickLegalName = () => {
    if (legalNamePathToRedirect) {
      redirect(legalNamePathToRedirect);
    }
  };

  return (
    <Stack spacing={3} data-cy="footer_container">
      <Stack
        direction={{ xs: 'column-reverse', md: 'row' }}
        justifyContent="space-between"
        spacing={2.5}
        alignItems={{ xs: 'center', md: 'unset' }}
      >
        <Stack spacing={4} flex={1}>
          <Stack
            alignItems={{ xs: 'center', md: 'unset' }}
            textAlign={{ xs: 'center', md: 'unset' }}
          >
            <Text variant="h3">About Us</Text>
            <Grid container spacing={2} rowSpacing={2}>
              <AboutUsLinks />
            </Grid>
          </Stack>
          {ToolsLinks && (
            <Stack
              alignItems={{ xs: 'center', md: 'unset' }}
              textAlign={{ xs: 'center', md: 'unset' }}
            >
              <Text variant="h3">Tools</Text>
              <Grid container spacing={2} rowSpacing={2}>
                <ToolsLinks />
              </Grid>
            </Stack>
          )}
          {SearchLinks && (
            <Stack
              alignItems={{ xs: 'center', md: 'unset' }}
              textAlign={{ xs: 'center', md: 'unset' }}
            >
              <Text variant="h3">Search</Text>
              <Grid container spacing={2} rowSpacing={2}>
                <SearchLinks />
              </Grid>
            </Stack>
          )}
          <Stack
            alignItems={{ xs: 'center', md: 'unset' }}
            textAlign={{ xs: 'center', md: 'unset' }}
          >
            <Text variant="h3">Helpful Links</Text>
            <Grid container spacing={2} rowSpacing={2}>
              {DynamicHelpLinks ? (
                <DynamicHelpLinks contactUsHandler={contactUsHandler} />
              ) : (
                helpLinks.map(
                  (link, index) =>
                    link.href && (
                      <Grid item xs={12} sm={3} key={index}>
                        <Stack
                          direction="row"
                          justifyContent={isMobile ? 'center' : 'flex-start'}
                        >
                          <StyledLink
                            target={link.clickHandler ? '' : '_blank'}
                            href={link.href}
                            onClick={link.clickHandler}
                            sx={{
                              display: 'inline-block',
                              width:
                                link.textContent === 'Advertise'
                                  ? '90px'
                                  : '180px',
                            }}
                          >
                            {link.textContent}
                          </StyledLink>
                          {link.textContent === 'Advertise' && (
                            <NewTag>NEW</NewTag>
                          )}
                        </Stack>
                      </Grid>
                    )
                )
              )}
            </Grid>
          </Stack>
          <Divider />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 4, md: 7 }}
          >
            <Stack alignItems={{ xs: 'center', md: 'unset' }}>
              <Text variant="h3">Stay connected</Text>
              <Stack direction="row" spacing={2}>
                {socialMedia.map((link, index) => (
                  <Link href={link.href} target="_blank" key={index}>
                    <Avatar src={link.imgLogoUrl} sx={{ fontSize: '2.5rem' }} />
                  </Link>
                ))}
              </Stack>
            </Stack>
            {hasMobileApps &&
              !isInternationalVinTest &&
              !isInInternationalLanguageTest && (
                <>
                  <Divider
                    orientation={isMobile ? 'horizontal' : 'vertical'}
                    flexItem
                  />
                  <Stack
                    spacing={1.5}
                    alignItems={{ xs: 'center', md: 'unset' }}
                    textAlign={{ xs: 'center', md: 'unset' }}
                  >
                    <Text variant="h3" sx={{ margin: 0 }}>
                      Try Our Mobile Apps
                    </Text>
                    <Text>
                      Access {brandName} with one-tap on iPhone, iPad and
                      Android
                    </Text>
                    <MobileAppsCtaButtons stackDirectionOverride="row" />
                  </Stack>
                </>
              )}
          </Stack>
        </Stack>
        {showCustomerServiceInfo && (
          <Box maxWidth={310}>
            <CustomerService />
          </Box>
        )}
      </Stack>
      <Divider />
      {customDisclaimer || (
        <Stack data-cy="footer_disclaimer" spacing={1.2}>
          <Text variant="body2">
            The information contained in any report and on our website is not
            necessarily 100% accurate, complete or up to date, nor a substitute
            for your own due diligence, especially concerning such sensitive
            items as criminal history, relatives, mortgages and liens. Our data
            comes from a wide variety of sources, but some municipalities and
            jurisdictions are slow to report and digitize their data, so we
            cannot guarantee or warrant full accuracy of ALL search results.
          </Text>
          <Text variant="body2">
            {brandName}'s mission is to give people easy and affordable access
            to public record information, but {brandName} does not provide
            private investigator services or consumer reports, and is not a
            consumer reporting agency per the{' '}
            <StyledLink href={fcraLink} underline="none">
              Fair Credit Reporting Act.
            </StyledLink>{' '}
            You may not use our site or service or the information provided to
            make decisions about employment, admission, consumer credit,
            insurance, tenant screening or any other purpose that would require
            FCRA compliance. For more information governing permitted and
            prohibited uses, please review our{' '}
            <StyledLink href={doDontsLink} underline="none">
              “Do's & Don'ts”
            </StyledLink>{' '}
            and our{' '}
            <StyledLink href={tosLink} underline="none">
              Terms & Conditions.
            </StyledLink>{' '}
          </Text>
        </Stack>
      )}
      <Text variant="body2">
        © {currentYear}{' '}
        <Text
          component="span"
          fontWeight="bold"
          sx={{
            textDecoration: 'underline',
            ':hover': {
              cursor: legalNamePathToRedirect ? 'pointer' : 'inherit',
            },
          }}
          variant="body2"
          onClick={handleOnClickLegalName}
        >
          {legalName}{' '}
        </Text>
        . All rights reserved. {brandName} ® and the {brandName} star mascot are
        registered trademarks of {legalName}.
      </Text>
      <Text variant="body2">
        <StyledLink href={tosLink} underline="none">
          Terms & Conditions
        </StyledLink>{' '}
        |{' '}
        <StyledLink href={privacyLink} underline="none">
          Privacy Policy
        </StyledLink>
      </Text>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        sx={{ userSelect: 'none' }}
      >
        <AccessibilityWidget />
        {showSecurityInfo && (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <StyledBox>PRIVACY SECURED</StyledBox>
            <CustomIcons.NortonIcon sx={{ fontSize: '4rem' }} />
          </Stack>
        )}
      </Stack>
      {isConfirmCancelModalOpen && (
        <ConfirmCancelModal
          isOpen={isConfirmCancelModalOpen}
          onClose={() => setIsConfirmCancelModalOpen(false)}
          onCancel={() => {
            setIsConfirmCancelModalOpen(false);
            setIsCancelFinalStepModalOpen(true);
          }}
        />
      )}
      {isCancelFinalStepModalOpen && (
        <CancelFinalStepModal
          isOpen={isCancelFinalStepModalOpen}
          onClose={() => setIsCancelFinalStepModalOpen(false)}
        />
      )}
    </Stack>
  );
};
